import type { ReactNode } from 'react'

import { approvalOutcomeStyles, chipStyle, StatusChip } from '@/features/pos'

import type { AuditLineState, AuditLogRow } from '../types'
import { quantity, settledAt, usd } from './report-format'
import { ReportTable } from './report-table'
import type { ReportColumn, ReportRow } from './report-table'

const COLUMNS: ReportColumn<AuditLogRow>[] = [
  { id: 'at', header: 'Time', value: (row) => settledAt(row.at) },
  { id: 'action', header: 'Action', value: (row) => row.actionLabel },
  {
    id: 'branch',
    header: 'Outlet',
    value: (row) => row.branchName,
    visibleWhen: (rows) => new Set(rows.map((row) => row.branchName)).size > 1,
  },
  { id: 'register', header: 'Register', value: (row) => row.registerName, hideWhenEmpty: true },
  { id: 'requestedBy', header: 'Requested by', value: (row) => row.requestedBy },
  {
    id: 'approvedBy',
    header: 'Approved by',
    value: (row) => row.approvedBy ?? (row.offline ? 'offline' : null),
    render: (row) =>
      row.approvedBy ? (
        <span className="text-accent">{row.approvedBy}</span>
      ) : (
        /* "— (offline)" rather than a blank. A till that applied the policy
           alone and a manager who said no are different events. */
        <span className="text-muted">
          — {row.offline && <span className="text-xs">(offline)</span>}
        </span>
      ),
  },
  {
    id: 'invoice',
    header: 'Invoice',
    value: (row) => row.invoiceNumber ?? row.orderNumber,
    hideWhenEmpty: true,
  },
  { id: 'item', header: 'Item', value: (row) => row.itemName, hideWhenEmpty: true },
  {
    id: 'change',
    header: 'Before → after',
    value: (row) => (row.before || row.after ? 'changed' : null),
    render: (row) => <LineChange before={row.before} after={row.after} />,
    hideWhenEmpty: true,
  },
  {
    id: 'amount',
    header: 'Amount',
    value: (row) => row.amount,
    render: (row) => (
      <span className="block text-end tabular-nums">
        {row.amount !== null
          ? usd(row.amount)
          : row.percentage !== null
            ? `${row.percentage}%`
            : '—'}
      </span>
    ),
    hideWhenEmpty: true,
  },
  {
    id: 'outcome',
    header: 'Outcome',
    value: (row) => row.outcome,
    render: (row) => <StatusChip style={chipStyle(approvalOutcomeStyles, row.outcome)} />,
  },
  { id: 'note', header: 'Note', value: (row) => row.note, hideWhenEmpty: true },
]

/**
 * R17 — every approval the tills asked for, and what it changed.
 *
 * Read-only, like the POS module's log beside it, and for the same reason: a
 * decided request is never rewritten. This is that log over a period, with
 * the invoice, the line and its figures before and after — the legacy POS
 * Authorization Log's columns, which is what an owner reaches for when a
 * discount looks wrong a week later.
 */
export function AuditLogTable({ rows, footer }: { rows: AuditLogRow[]; footer?: ReactNode }) {
  const lines: ReportRow<AuditLogRow>[] = rows.map((row, index) => ({
    id: `${row.at}:${row.action}:${index}`,
    data: row,
  }))

  return <ReportTable ariaLabel="Audit log" columns={COLUMNS} rows={lines} footer={footer} />
}

const FIELDS: { key: keyof AuditLineState; label: string; print: (value: number) => string }[] = [
  { key: 'price', label: 'Price', print: usd },
  { key: 'quantity', label: 'Qty', print: quantity },
  { key: 'discountPercent', label: 'Disc', print: (value) => `${value}%` },
  { key: 'discountAmount', label: 'Disc', print: usd },
  { key: 'net', label: 'Net', print: usd },
]

/**
 * `Price $4.50 → $3.00 · Net $9.00 → $6.00` — only what moved.
 *
 * A field present on one side only is still printed, against a dash, because
 * "a discount appeared" is precisely the change a log like this exists to show.
 */
function LineChange({
  before,
  after,
}: {
  before: AuditLineState | null
  after: AuditLineState | null
}) {
  const changes = FIELDS.filter(({ key }) => (before?.[key] ?? null) !== (after?.[key] ?? null))

  if (!changes.length) return <span className="text-muted">—</span>

  return (
    <span className="flex flex-col gap-0.5 text-xs tabular-nums">
      {changes.map(({ key, label, print }) => {
        const from = before?.[key]
        const to = after?.[key]

        return (
          <span key={key}>
            <span className="text-muted">{label}</span> {from === undefined ? '—' : print(from)} →{' '}
            {to === undefined ? '—' : print(to)}
          </span>
        )
      })}
    </span>
  )
}
